import {
  Button,
  Checkbox,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import {PolicyDetailDto} from 'Agent/Policies/api';
import policyApi from 'Agent/Policies/api/policyApi';
import {fetchPolicy} from 'Agent/Policies/ducks/policy';
import {PolicyDocumentType, PolicyDocumentTypes} from 'Agent/Policies/models/PolicyDocumentTypes';
import {PolicySource} from 'Agent/Policies/models/PolicySource';
import {TheFraudWarning} from 'Agent/Policies/models/TheFraudWarning';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import classnames from 'classnames';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {PAGE_ID, changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {createSnackbar} from 'Core/ducks/snackbar';
import {fromAsync, matchAsync} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {MouseEvent, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {useHistory, useParams} from 'react-router';
import {ReducerState} from 'rootReducer';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import Modal from 'Shared/components/Modal';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {hasEisFlag} from 'Shared/utilities/DataSourceUtility';
import './PolicyUploadDocument.sass';
import {getCurrentPage} from 'Core/components/Analytics/Analytics';

type PolicyUploadDocumentProps = WithSelectedAgencyProps;

const PolicyUploadDocument = (props: PolicyUploadDocumentProps) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const {selectedAgency} = props;
  const {policyId, policyNumber} = useParams<{policyId: string; policyNumber: string}>();
  const history = useHistory();
  const dispatch = useDispatch();

  const {policy} = useSelector((state: ReducerState) => state.policy);
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);

  const {enable_portals_virus_scan} = useSelector((state: ReducerState) => state.featureFlag);
  const {SIC_FileNet_Connectivity} = useSelector((state: ReducerState) => state.featureFlag);

  const policyDetails = fromAsync(policy);

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: false}));
  }, []);

  useEffect(() => {
    if (policyId && policyNumber) {
      dispatch(fetchPolicy(selectedAgency.encryptedIdentifier, policyId, policyNumber, hasEisFlag(policyDetails), selectedAgency.agentNumbers));
    }
  }, [policyId, policyNumber]);

  type FileUploadErrors = {
    selectedFormError: string | boolean;
    fileError: string | boolean;
  };

  const [policyUploadDocumentId, setPolicyUploadDocumentId] = useState('');
  const [file, setFile] = useState<File>();
  const [agreeToTerm, setAgreeToTerm] = useState(false);
  const [fraudWarningDialog, setFraudWarningDialog] = useState(false);
  const [errors, setErrors] = useState<FileUploadErrors>({selectedFormError: false, fileError: false});
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const location = useLocation();
  const MAX_SIZE = 10485760; // 10 MB in bytes.
  const onDropAccepted = (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
    setErrors({
      ...errors,
      fileError: false,
    });
  };
  const onDropRejected = (acceptedFiles: File[]) => {
    setFile(undefined);
    setErrors({
      ...errors,
      fileError: acceptedFiles[0].size > MAX_SIZE ? 'File Exceeds Maximum Allowed Size.' : 'File Format Not Accepted.',
    });
  };

  const {getRootProps, getInputProps} = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxSize: MAX_SIZE,
    accept: 'image/jpg, image/jpeg, image/tif, image/tiff, application/pdf',
  });

  const viewFraudWarningDialog = (event: MouseEvent) => {
    event.preventDefault();

    setFraudWarningDialog(true);
  };

  const uploadFile = () => {
    if (policyUploadDocumentId && file) {
      policyApi
        .uploadPolicyDocument(
          selectedAgency.encryptedIdentifier,
          file,
          policyUploadDocumentId,
          policyId!,
          policyNumber!,
          policyDetails.eisFlag
        )
        .then(result => {
          return pipe(
            result,
            E.fold(
              () => {
                if (SIC_FileNet_Connectivity) {
                  dispatch(createSnackbar('Virus scan failed and the document was not uploaded.'));
                } else {
                  dispatch(createSnackbar('Document upload failed.'));
                }
              },
              (response: any) => {
                if (SIC_FileNet_Connectivity) {
                  dispatch(createSnackbar('Virus scan was successful and the document was uploaded.'));
                } else if (enable_portals_virus_scan) {
                dispatch(
                  createSnackbar(
                    'Document received, virus scan in progress; If a virus is detected in your document, we will notify you at the email on file. You can also check the status of your uploads here: ',
                    true,
                    'Document Upload History',
                    '/agent/document-upload-history'
                  )
                );
                  } else {
                    dispatch(createSnackbar('Document successfully uploaded.'));
                  }
              }
            )
          );
        });

      history.push(PolicyRouteConstants.POLICY_DETAILS(policyId!, policyNumber!, hasEisFlag(policyDetails)));
    } else {
      setErrors({
        selectedFormError: !policyUploadDocumentId && 'Valid form required.',
        fileError: !file && 'Valid file required.',
      });
    }
  };

  const handlePolicyDocumentTypeChange = (e: React.ChangeEvent<{name?: string; value: unknown}>) => {
    setPolicyUploadDocumentId(e.target.value as string);
    setErrors({
      ...errors,
      selectedFormError: false,
    });
  };

  const renderDropzone = () => (
    <section className="policy-upload-document__file-container">
      <div {...getRootProps({className: 'policy-upload-document__dropzone'})}>
        <input {...getInputProps()} type="file" multiple={false} hidden data-testid="file-input-section" />
        <Typography data-testid="file-name-label" variant="body2" color={!!errors.fileError ? 'error' : 'initial'}>
          {(file && file.name) || errors.fileError || 'Drop File Here Or Click To Browse'}
        </Typography>
      </div>
    </section>
  );

  useEffect(() => {
    dispatch(changeAnalyticsState({page: {pageId: PAGE_ID[getCurrentPage(location.pathname)]}}));
  }, []);

  return matchAsync(policy, {
    Conflict: error => <ErrorPage error={error} />,
    Loaded: (result: PolicyDetailDto) => {
 
      const isWellness = (type: PolicyDocumentType) =>
        result.wellnessCoverage && type.label === 'Claim Documents - Wellness';

      const isOPT = (type: PolicyDocumentType) =>
        result.optCoverage && type.label === 'Claim Documents - OPT';

      const lifePolicyFilter = (type: PolicyDocumentType) =>
        !result.lifePlans || result.lifePlans.length === 0 || type.label !== 'Claim Document';
      const newYorkPolicyFilter = (type: PolicyDocumentType) =>
        result.source!.id === PolicySource.NewYork
          ? type.label !== 'HIPAA Authorization'
          : type.label !== 'HIPAA Authorization - New York';
      const isNonEISGroup = (type: PolicyDocumentType) =>
        !result.eisFlag
          ? result.source!.name === type.source // non-eis should follow API source (i.e. GROUP => 4 different categories under Claim Documents or ORDINARY => "Claim Document")
          : type.source === 'ORDINARY'; // Irrespective of GROUP or ORDINARY, output will always be "Claim Document"
      return (
        <>
          <Grid
            container
            spacing={3}
            className={classnames('policy-upload-document', {
              'policy-upload-document--desktop': isDesktop,
            })}
          >
            <Grid item container>
              <Typography variant="h6">Upload A Document</Typography>
            </Grid>
            <Grid item container spacing={3}>
              <Grid item>
                <Typography variant="subtitle2" data-dd-privacy="mask">
                  Policy Number: {result.policyNumber}
                </Typography>
              </Grid>
              {result.insured && (
                <Grid item>
                  <Typography variant="subtitle2" data-dd-privacy="mask">
                    Insured: {result.insured.fullName}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Paper className="policy-upload-document__container">
              <Grid item container spacing={3} direction="column">
                <Grid item>
                  <Typography variant="subtitle1">Uploads must be in the TIF, PDF, or JPG formats.</Typography>
                </Grid>
                <Grid item container spacing={3} justify="center">
                  {renderDropzone()}
                </Grid>
                <Grid item>
                  <FormControl
                    className="policy-upload-document__document-type-selector"
                    error={!!errors.selectedFormError}
                  >
                    <InputLabel id="formSelectorLabel">Select A Form</InputLabel>
                    <Select
                      id="document-type-Selector"
                      value={policyUploadDocumentId}
                      onChange={handlePolicyDocumentTypeChange}
                    >
                      {PolicyDocumentTypes.filter(lifePolicyFilter)
                        .filter(newYorkPolicyFilter)
                        .map(
                          (type, index) =>
                            (!type.source || (type.source && isNonEISGroup(type)) || isWellness(type) || isOPT(type)) && (
                              <MenuItem key={index} value={type.value}>
                                {type.label}
                              </MenuItem>
                            )
                        )}
                    </Select>
                    <FormHelperText>{errors.selectedFormError}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item container direction="column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="agree-to-term-checkbox"
                        color="primary"
                        checked={agreeToTerm}
                        onChange={() => setAgreeToTerm(!agreeToTerm)}
                        required
                      />
                    }
                    label="By Clicking this box and selecting SUBMIT, I hereby confirm that:"
                  />
                  <Grid item>
                    <ol>
                      <li>
                        The information I am submitting is for this policy/certificate and at the
                        policy/certificateholder's request.
                      </li>
                      <li>
                        The information I am submitting is true and complete to the best of my knowledge and belief.
                      </li>
                      <li>
                        I have read{' '}
                        <Link href="" onClick={viewFraudWarningDialog}>
                          the fraud warning
                        </Link>
                        {' '}for my state.
                      </li>
                    </ol>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button
                    color="primary"
                    onClick={() => {
                      dispatch(
                        changeAnalyticsState({
                          page: analytics.page,
                          ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.POLICIES_VIEW_UPLOAD_DOCUMENT_SUBMIT},
                        })
                      );
                      uploadFile();
                    }}
                    disabled={!agreeToTerm || !policyUploadDocumentId || !file}
                  >
                    Submit
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => {
                      dispatch(
                        changeAnalyticsState({
                          page: analytics.page,
                          ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.POLICIES_VIEW_UPLOAD_DOCUMENT_CANCEL},
                        })
                      );
                      history.push(
                        PolicyRouteConstants.POLICY_DETAILS(policyId!, policyNumber!, hasEisFlag(policyDetails))
                      );
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Modal title="Fraud Warning" open={fraudWarningDialog} onClose={() => setFraudWarningDialog(false)}>
            <DialogContent>
              <Typography className="policy-upload-document__fraud-warning" variant="body1">
                {TheFraudWarning.join('\n\n')}
              </Typography>
            </DialogContent>
          </Modal>
        </>
      );
    },
    Loading: () => <Loading />,
    ServerError: error => <ErrorPage error={error} />,
    Unauthorized: error => <ErrorPage error={error} />,
    NoData: () => <div className="paged-table__content">An error occurred. Please contact Administrator.</div>,
    NoContent: () => <div className="paged-table__content">No Results Available.</div>,
    NotStarted: () => <div className="paged-table__content">No Results Available.</div>,
  });
};

export default withSelectedAgency(PolicyUploadDocument);
